import React, { useLayoutEffect, useMemo, useRef } from "react";
import { registerSeek, unregisterSeek } from "../../utils/recorderSeekRegistry";

// Recorder mode only: a custom HTML element (HtmlContent full document) joins the
// recorder seek loop like RecorderCanvasImage. On every seek its iframe gets
// { type: "aidea-seek", t } with t = seconds since the box start; the runtime the
// middleware injects (scene-agent html-element.ts) pauses its CSS/WAAPI animations
// at t and replies { type: "aidea-seek-ack", t } to the parent after two rAF ticks.
// Normal playback never posts, so the element runs on wall-clock time.
// Cross-repo contract with aidea-middleware; until the runtime acks, the timeout applies.
const LOAD_TIMEOUT_MS = 3000;
const ACK_TIMEOUT_MS = 500;

const timeout = (promise, ms) =>
  new Promise((resolve) => {
    const tm = setTimeout(resolve, ms);
    promise.then(() => {
      clearTimeout(tm);
      resolve();
    });
  });

const waitAck = (win, t) =>
  new Promise((resolve) => {
    const done = () => {
      clearTimeout(tm);
      window.removeEventListener("message", onMsg);
      resolve();
    };
    const onMsg = (e) => {
      if (e.source === win && e.data && e.data.type === "aidea-seek-ack" && e.data.t === t) done();
    };
    const tm = setTimeout(done, ACK_TIMEOUT_MS);
    window.addEventListener("message", onMsg);
  });

export function makeHtmlSeekHandler(getWindow, getStartSec, getLoaded) {
  return async (sceneSec) => {
    // Boxes mount mid-loop: a seek posted before srcdoc loaded would be lost.
    if (getLoaded) await timeout(getLoaded(), LOAD_TIMEOUT_MS);
    const win = getWindow();
    if (!win) return;
    const t = Math.max(0, sceneSec - getStartSec());
    const ack = waitAck(win, t);
    // "*": a sandboxed srcdoc iframe has an opaque origin.
    win.postMessage({ type: "aidea-seek", t }, "*");
    await ack;
  };
}

export default function RecorderHtmlFrame({ srcDoc, startSec, sandbox, style, onLoad }) {
  const ref = useRef(null);
  const startRef = useRef(startSec);
  startRef.current = startSec;
  // Promise of the first load of the current srcDoc; fresh whenever srcDoc changes.
  // Later (guard-reset) loads resolve an already-resolved promise: harmless.
  const loaded = useMemo(() => {
    const l = {};
    l.promise = new Promise((r) => (l.resolve = r));
    return l;
  }, [srcDoc]);
  const loadedRef = useRef(loaded);
  useLayoutEffect(() => {
    loadedRef.current = loaded;
  }, [loaded]);
  // Layout effect: registered before the host's next seek reaches runAllSeeks.
  useLayoutEffect(() => {
    const id = `rhf-${Math.random().toString(36).slice(2)}`;
    registerSeek(
      id,
      makeHtmlSeekHandler(
        () => (ref.current ? ref.current.contentWindow : null),
        () => startRef.current || 0,
        () => loadedRef.current.promise
      )
    );
    return () => unregisterSeek(id);
  }, []);
  return (
    <iframe
      ref={ref}
      srcDoc={srcDoc}
      sandbox={sandbox}
      style={style}
      onLoad={(e) => {
        loaded.resolve();
        if (onLoad) onLoad(e);
      }}
      title="HTML content"
    />
  );
}
